import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Crosshair, Loader2, MapPin, Search, X, Building2, Globe2, DoorOpen } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CATEGORY_LABEL } from "@/lib/rides";
import { GPS_ERRORS, fromDirectory, inDirectory } from "@/lib/places";

function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function ResultRow({ icon: Icon, title, sub, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-muted">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold">{title}</span>
        <span className="block truncate text-xs text-muted-foreground">{sub}</span>
      </span>
    </button>
  );
}

function useGps(onFound) {
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState("");
  const locate = () => {
    setError("");
    if (!navigator.geolocation) return setError("Location isn't available on this device or browser. Search for the address instead.");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const { data } = await api.get("/geo/reverse", { params: { lat: coords.latitude, lng: coords.longitude } });
          if (!data.resolved) toast.warning("We found you but couldn't match a street address. Drag the pin to adjust or search instead.");
          else toast.success("Location found. Drag the map pin if it's a little off.");
          if (!data.in_service_area) toast.warning("You're outside the Scoopz home area. Long trips may need an admin quote.");
          onFound(data);
        } catch (e) {
          setError(`We couldn't look up an address for your location. ${formatApiError(e)}`);
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        setError(GPS_ERRORS[err.code] || "We couldn't get your location. Search for the address instead.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };
  return { locating, error, locate };
}

function GpsButton({ kind, gps }) {
  return (
    <button type="button" onClick={gps.locate} disabled={gps.locating} data-testid={`${kind}-gps-button`}
      className="flex shrink-0 items-center gap-1.5 rounded-full border border-accent/40 bg-accent/10 px-3 py-1.5 text-xs font-semibold text-accent transition-colors hover:bg-accent/20 disabled:opacity-60">
      {gps.locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5" />}
      <span className="hidden sm:inline">Use Current Location</span><span className="sm:hidden">Current Location</span>
    </button>
  );
}

const DOT = {
  pickup: "bg-neon-cyan shadow-[0_0_8px_hsl(var(--neon-cyan))]",
  dropoff: "bg-neon-pink shadow-[0_0_8px_hsl(var(--neon-pink))]",
  other: "bg-neon-violet shadow-[0_0_8px_hsl(var(--neon-violet))]",
};

function SelectedPlace({ kind, label, value, onChange, details, dot }) {
  return (
    <div className="rounded-2xl border bg-card px-4 py-3" data-testid={`${kind}-selected`}>
      <div className="flex items-center gap-3">
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dot}`} />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {label}{value.location_source === "gps" && " · GPS"}{value.pin_adjusted && " · pin adjusted"}
          </p>
          <p className="truncate text-sm font-semibold" data-testid={`${kind}-selected-name`}>{value.name}</p>
          {(value.formatted_address || value.address) && (
            <p className="truncate text-xs text-muted-foreground" data-testid={`${kind}-selected-address`}>{value.formatted_address || value.address}</p>
          )}
        </div>
        <button type="button" onClick={() => onChange(null)} data-testid={`${kind}-clear-button`}
          className="grid h-8 w-8 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
      {details && (
        <label className="mt-2 flex items-center gap-2 rounded-xl bg-muted/60 px-3 focus-within:ring-2 focus-within:ring-primary/30">
          <DoorOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <input data-testid={`${kind}-unit-input`} maxLength={80} value={value.unit_number || ""}
            onChange={(e) => onChange({ ...value, unit_number: e.target.value || null })}
            placeholder="Apt, suite, building, unit or gate code (optional)"
            className="h-9 flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground" />
        </label>
      )}
    </div>
  );
}

function CategoryFilter({ kind, category, setCategory }) {
  return (
    <div className="relative">
      <div className="flex gap-1.5 overflow-x-auto px-3 pb-2 pr-10 [scrollbar-width:thin]" data-testid={`${kind}-category-filter`}>
        {[["", "All"], ...Object.entries(CATEGORY_LABEL).filter(([k]) => k !== "other")].map(([k, v]) => (
          <button key={k || "all"} type="button" onClick={() => setCategory(k)} data-testid={`${kind}-category-${k || "all"}`}
            className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${category === k ? "border-primary bg-primary text-primary-foreground" : "border-white/15 text-muted-foreground hover:text-foreground"}`}>
            {v}
          </button>
        ))}
      </div>
      <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-card to-transparent" />
    </div>
  );
}

function AddressResults({ kind, q, dir, onPick }) {
  const live = useQuery({
    queryKey: ["geo", q],
    queryFn: () => api.get("/geo/search", { params: { q } }).then((r) => r.data),
    retry: false,
  });
  const rows = (live.data || []).filter((p) => !inDirectory(p, dir));
  return (
    <>
      <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Addresses across Texas</p>
      {live.isFetching && <Loader2 className="mx-3 my-2 h-4 w-4 animate-spin text-muted-foreground" data-testid={`${kind}-address-loading`} />}
      {rows.map((p, i) => (
        <ResultRow key={`${p.place_id}-${i}`} icon={Globe2} title={p.name} sub={p.formatted_address}
          testid={`${kind}-osm-result`} onClick={() => onPick(p)} />
      ))}
      {live.isError && <p className="px-3 py-2 text-xs text-destructive" data-testid={`${kind}-address-error`}>{formatApiError(live.error)}</p>}
      {!live.isFetching && !live.isError && rows.length === 0 && (
        <p className="px-3 py-2 text-xs text-muted-foreground" data-testid={`${kind}-address-empty`}>No Texas addresses found. Try adding the street number and city.</p>
      )}
    </>
  );
}

export function LocationPicker({ kind, label, value, onChange, allowGps, details = true, placeholder }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const q = useDebounced(query.trim());
  const stale = query.trim() !== q;
  const dot = DOT[kind] || DOT.other;

  const directory = useQuery({
    queryKey: ["locations", q, category],
    queryFn: () => api.get("/locations", { params: { q: q || undefined, category: category || undefined, limit: category ? 60 : q ? 8 : 6 } }).then((r) => r.data),
    enabled: open,
  });

  const pick = (p) => {
    onChange(p);
    setQuery("");
    setOpen(false);
  };
  const pickAddress = (p) => {
    pick(p);
    api.post("/locations/suggest", p).catch(() => {});
  };
  const gps = useGps(pick);

  if (value) return <SelectedPlace kind={kind} label={label} value={value} onChange={onChange} details={details} dot={dot} />;

  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-2xl border bg-card py-1.5 pl-4 pr-1.5 focus-within:border-primary focus-within:ring-4 focus-within:ring-primary/15">
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dot}`} />
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          data-testid={`${kind}-search-input`}
          className="h-10 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          placeholder={placeholder || (kind === "pickup" ? "Pickup address" : kind === "dropoff" ? "Where to?" : "Search for a stop")}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => setQuery(e.target.value)}
        />
        {allowGps && <GpsButton kind={kind} gps={gps} />}
      </div>
      {gps.error && <p className="mt-2 rounded-xl bg-destructive/10 px-3 py-2 text-xs text-destructive" data-testid={`${kind}-gps-error`}>{gps.error}</p>}
      {open && (
        <div className="mt-2 rounded-2xl border bg-card p-2 shadow-lg" data-testid={`${kind}-results`}>
          <div className="flex items-center justify-between px-3 pb-1 pt-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {category ? CATEGORY_LABEL[category] : q ? "Scoopz directory" : "Popular spots"}
            </p>
            <button type="button" onClick={() => setOpen(false)} className="text-xs font-semibold text-muted-foreground hover:text-foreground" data-testid={`${kind}-results-close`}>
              Close
            </button>
          </div>
          <CategoryFilter kind={kind} category={category} setCategory={setCategory} />
          {(directory.isLoading || stale) && <Loader2 className="mx-3 my-2 h-4 w-4 animate-spin text-muted-foreground" data-testid={`${kind}-results-loading`} />}
          {!stale && directory.data?.map((l) => (
            <ResultRow key={l.id} icon={Building2} title={l.name} testid={`${kind}-directory-result`}
              sub={`${l.source_category || CATEGORY_LABEL[l.category] || l.category} · ${l.city}`} onClick={() => pick(fromDirectory(l))} />
          ))}
          {(q || category) && !stale && !directory.isLoading && !directory.data?.length && (
            <p className="px-3 py-2 text-xs text-muted-foreground">No directory matches.</p>
          )}
          {q.length >= 3 && !stale && (
            <AddressResults kind={kind} q={q} onPick={pickAddress} dir={directory.data || []} />
          )}
          {q.length < 3 && (
            <p className="flex items-center gap-1 px-3 pb-1 pt-2 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" /> Type 3+ characters to search any Texas address
            </p>
          )}
        </div>
      )}
    </div>
  );
}
